<template>
  <div class="product-categories-page">

    <!-- ProductCategoriesRegistry сам знает какая категория выбрана и эмитит её наверх,
         Page просто хранит выбранную категорию и пробрасывает в форму.
         null означает "ничего не выбрано" — показываем заглушку "Выберите действие в меню слева". -->

    <ProductCategoriesRegistry
      ref="listRef"
      v-model="selectedCategory"
    />

    <section class="category-form">
      <article
        v-if="!selectedCategory"
        class="no-data"
      >
        <p>Выберите действие в меню слева</p>

        <VButton
          icon-postfix
          theme="link"
          @click="createRootCategory"
        >
          <template #icon>
            <VIcon material="add" />
          </template>
          Создать категорию
        </VButton>
      </article>

      <!-- Ключ для новых категорий (id === null) включает selectionGeneration:
           без него последовательные «новые» категории (создание после несохранённого
           создания, копия после копии) имели одинаковый ключ 'new', Vue переиспользовал
           инстанс EditProductCategory и localProductCategory не переинициализировался. -->
      <EditProductCategory
        v-if="selectedCategory"
        :key="selectedCategory.id ?? 'new-' + selectionGeneration"
        :product-category="selectedCategory"
        @update:productCategory="onCategoriesChange"
        @close="onCloseEditCategory"
      />
    </section>
  </div>
</template>

<style lang="scss" src="./ProductCategoriesPage.scss" />

<script lang="ts">

import ProductCategory           from '@/domains/ProductCategories/ProductCategory';
import ProductCategoriesService  from '@/domains/ProductCategories/ProductCategoriesService';

import ProductCategoriesRegistry from '@/domains/ProductCategories/components/ProductCategoriesRegistry/ProductCategoriesRegistry.vue';
import EditProductCategory       from '@/domains/ProductCategories/components/EditProductCategory/EditProductCategory.vue';

export default defineComponent({
  name: 'ProductCategoriesPage',

  components: {
    ProductCategoriesRegistry,
    EditProductCategory,
  },

  data() {
    return {
      selectedCategory: null as ProductCategory | null,
      selectionGeneration: 0,
    };
  },

  watch: {
    /** Registry пишет в selectedCategory напрямую через v-model,
     * поэтому инкремент поколения делаем в watch, а не в методах —
     * иначе присвоения из createCategory/createCategoryCopy пролетят мимо. */
    selectedCategory() {
      this.selectionGeneration++;
    },
  },

  methods: {
    /** Создание корневой категории, когда форма пуста.
     *  Берём первую реальную root-категорию как parent — поведение из исходника. */
    createRootCategory() {
      const productCategoriesMap = ProductCategoriesService.getProductCategoriesMap();
      const rootList = (this.$refs.listRef as any).rootProductCategories;

      if (!rootList || rootList.length === 0) {
        return;
      }

      const rootCategory = productCategoriesMap.get(rootList[1]?.id ?? rootList[0].id);
      this.selectedCategory = ProductCategory.fromParentCategory(rootCategory);
    },

    async onCategoriesChange(payload: { id: number } | null) {
      /** Защита от невалидного payload: раньше сюда мог прилететь null
       * при неудачном сохранении, деструктуризация падала на входе
       * и selectedCategory залипал в несогласованном состоянии. */
      if (!payload || !payload.id) {
        return;
      }

      const id = payload.id;

      this.selectedCategory = null;

      /** reload и showEditCategory летят параллельно — два независимых запроса.
       * showEditCategory подтянет полную категорию с units/characteristics,
       * а reload параллельно обновит дерево слева и развернёт родителей. */

      const reloadPromise = (async () => {
        await (this.$refs.listRef as any).reloadProductCategories();
        (this.$refs.listRef as any).expandCategory(id);
      })();

      await Promise.all([reloadPromise, this.showEditCategory(id)]);
    },

    onCloseEditCategory() {
      this.selectedCategory = null;
    },

    async showEditCategory(editCategoryId: number) {
      this.selectedCategory = await ProductCategoriesService.get(editCategoryId, {
        requireAllowedUnits:    true,
        requireCharacteristics: true,
      });
    },
  },
});
</script>
